import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackLeadConversion } from "../lib/gtag";
import { HEADER_IMG } from "../data/site";

const inputCls =
  "w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground rounded-none focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-shadow";
const labelCls = "block font-mono uppercase text-[11px] tracking-[0.15em] text-muted-foreground mb-2";

export default function BuyerRegistration() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [fileName, setFileName] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData(e.target);
      await api.post("/buyer-registration", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackLeadConversion("buyer-registration");
      setDone(true);
      toast.success("Thank you! Your request has been submitted successfully. Our team will review it and get back to you shortly.");
      e.target.reset();
      setFileName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="buyer-page">
      <PageHeader
        overline="Buyer Registration"
        title="Register as a buyer."
        subtitle="Tell us what you import. Register your requirement and our sourcing engineers will match you with the right Indian manufacturers."
        bgImage={HEADER_IMG.buyer}
      />
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            data-testid="buyer-success" className="border border-border bg-secondary p-12 text-center">
            <CheckCircle2 size={48} className="text-brand-orange mx-auto mb-6" />
            <h2 className="font-display font-black text-3xl mb-3">Registration Received!</h2>
            <p className="text-muted-foreground mb-8">Our team is reviewing your requirement and will be in touch soon.</p>
            <button onClick={() => setDone(false)} className="border border-foreground px-6 py-3 font-mono uppercase text-[11px] tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors">Submit Another</button>
          </motion.div>
        ) : (
          <form onSubmit={onSubmit} data-testid="buyer-form" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div><label className={labelCls}>Company Name *</label><input data-testid="buyer-company" name="company_name" required className={inputCls} placeholder="Your company" /></div>
              <div><label className={labelCls}>Country *</label><input data-testid="buyer-country" name="country" required className={inputCls} placeholder="e.g. United States" /></div>
              <div><label className={labelCls}>Contact Person *</label><input data-testid="buyer-contact" name="contact_person" required className={inputCls} placeholder="Full name" /></div>
              <div><label className={labelCls}>Email *</label><input data-testid="buyer-email" type="email" name="email" required className={inputCls} placeholder="you@company.com" /></div>
              <div><label className={labelCls}>Phone *</label><input data-testid="buyer-phone" name="phone" required className={inputCls} placeholder="+1 ..." /></div>
              <div><label className={labelCls}>Commodity Required *</label><input data-testid="buyer-commodity" name="commodity" required className={inputCls} placeholder="e.g. CNC machined parts" /></div>
              <div><label className={labelCls}>Annual Requirement</label><input data-testid="buyer-annual" name="annual_requirement" className={inputCls} placeholder="e.g. 100,000 units/year" /></div>
              <div><label className={labelCls}>Target Price</label><input data-testid="buyer-target" name="target_price" className={inputCls} placeholder="e.g. $2.50/unit" /></div>
            </div>
            <div><label className={labelCls}>Message</label><textarea data-testid="buyer-message" name="message" rows={4} className={inputCls} placeholder="Specifications, tolerances, timelines..." /></div>
            <div>
              <label className={labelCls}>Upload Drawing / PDF</label>
              <label className="flex items-center gap-3 border border-dashed border-border px-4 py-4 cursor-pointer hover:border-brand-orange transition-colors">
                <Upload size={18} className="text-brand-orange" />
                <span className="text-muted-foreground text-sm truncate">{fileName || "Choose a file (PDF, DWG, PNG, JPG)"}</span>
                <input data-testid="buyer-file" type="file" name="drawing" accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.step,.stp"
                  className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
              </label>
            </div>
            <button data-testid="buyer-submit" type="submit" disabled={loading}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting</> : "Submit RFQ"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
